import React from 'react'
import {
  Navigate,
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router-dom'

type CompatRouteProps = {
  children?: React.ReactNode
  component?: React.ComponentType
  exact?: boolean
  path?: string
  render?: () => React.ReactNode
}

function matches(pathname: string, path?: string, exact = false) {
  return !path || Boolean(matchPath({ path, end: exact }, pathname))
}

export function Route({
  children,
  component: Component,
  exact,
  path,
  render,
}: CompatRouteProps) {
  const { pathname } = useLocation()

  if (!matches(pathname, path, exact)) return null
  if (Component) return <Component />
  if (render) return <>{render()}</>
  return <>{children}</>
}

export function Switch({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const selected = React.Children.toArray(children).find((child) => {
    if (!React.isValidElement<CompatRouteProps>(child)) return false
    return matches(pathname, child.props.path, child.props.exact)
  })

  return <>{selected ?? null}</>
}

export function Redirect({ to }: { to: string }) {
  return <Navigate replace to={to} />
}

export function useHistory() {
  const navigate = useNavigate()
  return React.useMemo(
    () => ({
      push: (to: string) => navigate(to),
    }),
    [navigate]
  )
}
